import {getLocalStorageItem, setLocalStorageItem} from "@/lib/storageUtils";

const AFFILIATE_BASE_URL = "https://app.braiv.co/";
const AFFILIATE_REF_STORAGE_KEY = "affiliateRefCode";

const REF_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

const cleanRefCode = (value?: string | null): string | null => {
    const trimmed = (value || "").trim();
    if (!trimmed) return null;
    const withoutBraces = trimmed.replace(/^\{+|\}+$/g, "").trim();
    return REF_CODE_PATTERN.test(withoutBraces) ? withoutBraces : null;
};

export function persistAffiliateRefCode(refCode?: string | null): void {
    const code = cleanRefCode(refCode);
    if (!code) return;
    setLocalStorageItem(AFFILIATE_REF_STORAGE_KEY, code);
}

export function readPersistedAffiliateRefCode(): string | null {
    return cleanRefCode(getLocalStorageItem(AFFILIATE_REF_STORAGE_KEY));
}

export function appendAffiliateRefToPath(path: string, refCode?: string | null): string {
    const code = cleanRefCode(refCode);
    if (!code) return path;

    const hashIndex = path.indexOf("#");
    const hash = hashIndex >= 0 ? path.slice(hashIndex) : "";
    const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path;
    const separator = pathWithoutHash.includes("?") ? "&" : "?";
    return `${pathWithoutHash}${separator}ref=${encodeURIComponent(code)}${hash}`;
}

export function getAffiliateRefCodeFromRuntime(): string | null {
    if (typeof window === "undefined") return null;

    // Prefer explicit URL query when present.
    const queryRef = cleanRefCode(new URLSearchParams(window.location.search).get("ref"));
    if (queryRef) {
        persistAffiliateRefCode(queryRef);
        return queryRef;
    }

    const globalReferral = (window as any).tolt_referral;
    const toltRef =
        cleanRefCode(typeof globalReferral === "string" ? globalReferral : null) ||
        cleanRefCode(globalReferral?.id) ||
        cleanRefCode(globalReferral?.ref) ||
        cleanRefCode(globalReferral?.referral) ||
        cleanRefCode(globalReferral?.code);

    if (toltRef) {
        persistAffiliateRefCode(toltRef);
        return toltRef;
    }

    return readPersistedAffiliateRefCode();
}

const parseRefFromPotentialToltLink = (value: string): string | null => {
    // Accept links with or without protocol, e.g.:
    // https://braiv.tolt.io/abc123, braiv.tolt.io/abc123, tolt.io/abc123
    const match = value.match(/(?:https?:\/\/)?(?:[^\/]+\.)?tolt\.io\/([A-Za-z0-9_-]+)/i);
    return cleanRefCode(match?.[1] || null);
};

const REF_QUERY_KEYS = ["ref", "referral", "referral_code", "referralCode", "code", "r"];

export function extractAffiliateRefCodeFromLink(rawLink?: string | null): string | null {
    const input = (rawLink || "").trim();
    if (!input) return null;

    const tryParseUrl = (value: string): URL | null => {
        try {
            return new URL(value);
        } catch {
            return null;
        }
    };

    // Try absolute URL first.
    let parsed = tryParseUrl(input);
    // Then protocol-less URLs (common in manually entered referral links).
    if (!parsed && /^[^/\s]+\.[^/\s]+/.test(input)) {
        parsed = tryParseUrl(`https://${input}`);
    }

    if (parsed) {
        for (const key of REF_QUERY_KEYS) {
            const candidate = cleanRefCode(parsed.searchParams.get(key));
            if (candidate) return candidate;
        }

        // Support path-style code links on both Tolt and app domains.
        const pathCode = cleanRefCode(parsed.pathname.split("/").filter(Boolean).pop());
        const host = parsed.hostname.toLowerCase();
        if (pathCode && (host.includes("tolt.io") || host.includes("braiv.co") || host.includes("app.braiv.co"))) {
            return pathCode;
        }
    }

    // Tolt link fallback regex (protocol-less variants).
    const toltPathCode = parseRefFromPotentialToltLink(input);
    if (toltPathCode) {
        return toltPathCode;
    }

    // Plain code fallback.
    return cleanRefCode(input);
}

export function normalizeAffiliateReferralLink(rawLink?: string | null): string | null {
    const input = (rawLink || "").trim();
    if (!input) return null;

    const toCanonical = (refCode: string) => {
        const code = refCode.trim();
        if (!code) return null;
        return `${AFFILIATE_BASE_URL}?ref=${encodeURIComponent(code)}`;
    };

    const refCode = extractAffiliateRefCodeFromLink(input);
    if (refCode) {
        return toCanonical(refCode);
    }

    return input;
}
